import { Trans, useLingui } from '@lingui/react/macro'
import {
  DismissButton,
  formatOverlayTimestamp,
  overlayPanelSurface,
} from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { SensorSelect } from 'features/timeSeries/components/chartTools/SensorSelect'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import {
  findNearestIndexByTime,
  findNearestValidIndex,
  PointAnnotation,
} from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import { getFloatingPlacement } from 'features/timeSeries/components/rulerTool/overlayPositioning'
import {
  getSeriesInfoFromUPlot,
  VisibleSensorInfo,
} from 'features/timeSeries/components/rulerTool/useRulerTool'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

const OVERLAY_EDGE_PADDING = 8
const LABEL_GAP = 10
const LABEL_VERTICAL_OFFSET = 18
const FALLBACK_LABEL_WIDTH = 220
const FALLBACK_LABEL_HEIGHT = 68

const INTERACTIVE_ELEMENT_SELECTOR = 'button, [role="menuitem"]'

const OverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  z-index: 18;
  pointer-events: none;
  user-select: none;
`

const InteractionLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: all;
  cursor: crosshair;
`

const AnnotationAnchor = styled.div`
  position: absolute;
  pointer-events: all;
  z-index: 23;
`

const PointMarker = styled.div<{
  $color: string
  $isPending?: boolean
  $isDragging?: boolean
}>`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--card);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
  background: ${({ $color }) => $color};
  opacity: ${({ $isPending }) => ($isPending ? 0.8 : 1)};
  transform: translate(-50%, -50%);
  pointer-events: all;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  z-index: 24;
`

const AnnotationBubble = styled.div<{ $isDragging?: boolean }>`
  ${overlayPanelSurface}
  position: absolute;
  min-width: 180px;
  max-width: 260px;
  padding: 7px 10px;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
`

const BubbleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const BubbleHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
`

const BubbleValue = styled.div`
  margin-top: 4px;
  color: ${CHART_COLORS.TOOLTIP_VALUE_COLOR};
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`

const BubbleTimestamp = styled.div`
  margin-top: 2px;
  color: ${CHART_COLORS.TOOLTIP_TIME_COLOR};
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const PendingMessage = styled.div`
  margin-top: 4px;
  color: ${CHART_COLORS.TOOLTIP_NAME_COLOR};
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
`

interface PlotBounds {
  overLeft: number
  overRight: number
  overTop: number
  overBottom: number
}

interface RenderedAnnotation {
  id: string
  sensorId: string | null
  name: string
  color: string
  unit?: string
  timestamp: number
  value: number
  xPixelOnOverlay: number
  yPixelOnOverlay: number
}

interface AnnotationRenderDebugState {
  status: 'rendered' | 'rendered-from-cache' | 'hidden'
  reason: string
  sensorId: string | null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function formatValue(value: number, unit?: string): string {
  const formatted =
    Math.abs(value) >= 1000
      ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4,
        })

  return unit ? `${formatted} ${unit}` : formatted
}

function useObservedElementSize<T extends HTMLElement>(
  element: T | null
): {
  width: number
  height: number
} {
  const [size, setSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    if (!element) {
      setSize({ width: 0, height: 0 })
      return
    }

    const updateSize = () => {
      const nextSize = {
        width: element.offsetWidth,
        height: element.offsetHeight,
      }

      setSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize
      )
    }

    updateSize()
    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [element])

  return size
}

function usePlotBounds(
  uPlotRef: React.RefObject<uPlot | null>,
  rootRef: React.RefObject<HTMLDivElement | null>
): PlotBounds | null {
  const [bounds, setBounds] = useState<PlotBounds | null>(null)

  useLayoutEffect(() => {
    let rafId: number | null = null
    const computeBounds = () => {
      const u = uPlotRef.current
      const root = rootRef.current
      // uPlot is created in a passive effect (useUPlotChart) that runs after
      // this layout effect. On the load path the overlay mounts as soon as a
      // saved chart restores annotations — typically before the time-series
      // fetch has built uPlot — so `u`/`u.over` is not ready on the first pass,
      // and nothing else recomputes bounds once uPlot arrives (the rootRef
      // ResizeObserver never fires for a uPlot mount). Retry on the next frame
      // until the plot area is measurable; otherwise a loaded annotation keeps
      // bounds=null and never renders, even after a pan/zoom.
      if (!u || !root || !u.over) {
        rafId = requestAnimationFrame(computeBounds)
        return
      }

      const rootRect = root.getBoundingClientRect()
      const overRect = u.over.getBoundingClientRect()

      setBounds({
        overLeft: overRect.left - rootRect.left,
        overRight: overRect.right - rootRect.left,
        overTop: overRect.top - rootRect.top,
        overBottom: overRect.bottom - rootRect.top,
      })
    }

    computeBounds()

    const resizeObserver = new ResizeObserver(computeBounds)
    if (rootRef.current) resizeObserver.observe(rootRef.current)

    return () => {
      resizeObserver.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [rootRef, uPlotRef])

  return bounds
}

interface PositionedAnnotationProps {
  annotationId: string
  overlayWidth: number
  overlayHeight: number
  color: string
  name: string
  value: number
  unit?: string
  timestamp: number
  xPixelOnOverlay: number
  yPixelOnOverlay: number
  sensorId: string | null
  visibleSensors: VisibleSensorInfo[]
  dragOffsetX: number
  dragOffsetY: number
  isBubbleDragging: boolean
  isPointDragging: boolean
  onBubbleDragStart: (
    annotationId: string,
    startClientX: number,
    startClientY: number
  ) => void
  onPointDragStart: (annotationId: string) => void
  onSetSensorId: (annotationId: string, sensorId: string) => void
  onRemove?: () => void
}

const PositionedAnnotation: React.FC<PositionedAnnotationProps> = ({
  annotationId,
  overlayWidth,
  overlayHeight,
  color,
  name,
  value,
  unit,
  timestamp,
  xPixelOnOverlay,
  yPixelOnOverlay,
  sensorId,
  visibleSensors,
  dragOffsetX,
  dragOffsetY,
  isBubbleDragging,
  isPointDragging,
  onBubbleDragStart,
  onPointDragStart,
  onSetSensorId,
  onRemove,
}) => {
  const { t } = useLingui()

  const [bubbleElement, setBubbleElement] = useState<HTMLDivElement | null>(
    null
  )
  const { width, height } = useObservedElementSize(bubbleElement)
  const bubbleWidth = width || FALLBACK_LABEL_WIDTH
  const bubbleHeight = height || FALLBACK_LABEL_HEIGHT
  const placement = getFloatingPlacement({
    anchorLeft: xPixelOnOverlay,
    anchorRight: xPixelOnOverlay,
    containerWidth: overlayWidth,
    floatingWidth: bubbleWidth,
    gap: LABEL_GAP,
    edgePadding: OVERLAY_EDGE_PADDING,
    preferredSide: 'right',
  })
  const hasSelectedSensor = sensorId !== null
  const left = clamp(
    placement.left + dragOffsetX,
    OVERLAY_EDGE_PADDING,
    Math.max(
      OVERLAY_EDGE_PADDING,
      overlayWidth - bubbleWidth - OVERLAY_EDGE_PADDING
    )
  )
  const baseTop = clamp(
    yPixelOnOverlay - LABEL_VERTICAL_OFFSET,
    OVERLAY_EDGE_PADDING,
    Math.max(
      OVERLAY_EDGE_PADDING,
      overlayHeight - bubbleHeight - OVERLAY_EDGE_PADDING
    )
  )
  const top = clamp(
    baseTop + dragOffsetY,
    OVERLAY_EDGE_PADDING,
    Math.max(
      OVERLAY_EDGE_PADDING,
      overlayHeight - bubbleHeight - OVERLAY_EDGE_PADDING
    )
  )
  const markerColor = hasSelectedSensor ? color : 'rgba(255, 255, 255, 0.92)'
  const removeTitle = hasSelectedSensor
    ? t`Remove ${name}`
    : t`Remove annotation`
  const handleBubbleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return

      const eventTarget =
        event.target instanceof HTMLElement ? event.target : null
      if (eventTarget?.closest(INTERACTIVE_ELEMENT_SELECTOR)) return

      event.preventDefault()
      event.stopPropagation()
      onBubbleDragStart(annotationId, event.clientX, event.clientY)
    },
    [annotationId, onBubbleDragStart]
  )
  const handleMarkerMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return

      event.preventDefault()
      event.stopPropagation()
      onPointDragStart(annotationId)
    },
    [annotationId, onPointDragStart]
  )

  return (
    <>
      <PointMarker
        data-testid={`point-annotation-marker-${annotationId}`}
        $color={markerColor}
        $isPending={!hasSelectedSensor}
        $isDragging={isPointDragging}
        onMouseDown={handleMarkerMouseDown}
        style={{ left: xPixelOnOverlay, top: yPixelOnOverlay }}
      />
      <AnnotationAnchor
        data-testid={`point-annotation-anchor-${annotationId}`}
        style={{ left, top }}>
        <AnnotationBubble
          ref={setBubbleElement}
          data-testid={`point-annotation-bubble-${annotationId}`}
          onMouseDown={handleBubbleMouseDown}
          $isDragging={isBubbleDragging}>
          <BubbleHeader>
            <BubbleHeaderContent>
              <SensorSelect
                options={visibleSensors}
                selectedSensorId={sensorId}
                onSelect={(nextSensorId) =>
                  onSetSensorId(annotationId, nextSensorId)
                }
                placeholder={t`Select a pen`}
                maxWidth={180}
              />
            </BubbleHeaderContent>
            {onRemove && (
              <ChartTooltip label={removeTitle} side='top'>
                <DismissButton onClick={onRemove} aria-label={removeTitle}>
                  ×
                </DismissButton>
              </ChartTooltip>
            )}
          </BubbleHeader>
          {hasSelectedSensor ? (
            <>
              <BubbleValue>{formatValue(value, unit)}</BubbleValue>
              <BubbleTimestamp>
                {formatOverlayTimestamp(timestamp)}
              </BubbleTimestamp>
            </>
          ) : (
            <>
              <PendingMessage>
                <Trans>Select a pen to snap this annotation.</Trans>
              </PendingMessage>
              <BubbleTimestamp>
                {formatOverlayTimestamp(timestamp)}
              </BubbleTimestamp>
            </>
          )}
        </AnnotationBubble>
      </AnnotationAnchor>
    </>
  )
}

interface PointAnnotationOverlayProps {
  uPlotRef: React.RefObject<uPlot | null>
  isActive: boolean
  /**
   * Draw the persisted annotations. False while a view-changing load is in
   * flight for the current window, so a saved annotation appears at its final
   * projected position once the data settles instead of projecting against the
   * preserved previous-window data (or a cached pixel) and then jumping. The
   * interaction layer is unaffected.
   */
  showPersisted: boolean
  visibleSensors: VisibleSensorInfo[]
  unitMapping: Record<string, string>
  sensorRanges: Record<string, { min?: number; max?: number; unit: string }>
  annotations: PointAnnotation[]
  onRemoveAnnotation: (annotationId: string) => void
  onSetAnnotationSensorId: (annotationId: string, sensorId: string) => void
  onUpdateAnnotationAnchor: (
    annotationId: string,
    nextAnchor: { anchorTimestamp: number; anchorYRatio: number }
  ) => void
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave: () => void
}

interface AnnotationOffset {
  x: number
  y: number
}

interface BubbleDragState {
  annotationId: string
  startClientX: number
  startClientY: number
  startOffsetX: number
  startOffsetY: number
}

interface PointDragState {
  annotationId: string
}

export const PointAnnotationOverlay: React.FC<PointAnnotationOverlayProps> = ({
  uPlotRef,
  isActive,
  showPersisted,
  visibleSensors,
  unitMapping,
  sensorRanges,
  annotations,
  onRemoveAnnotation,
  onSetAnnotationSensorId,
  onUpdateAnnotationAnchor,
  onMouseMove,
  onMouseDown,
  onMouseLeave,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const lastRenderedAnnotationsRef = useRef<Record<string, RenderedAnnotation>>(
    {}
  )
  const [projectionVersion, setProjectionVersion] = useState(0)
  const [annotationOffsets, setAnnotationOffsets] = useState<
    Record<string, AnnotationOffset>
  >({})
  const [bubbleDragState, setBubbleDragState] =
    useState<BubbleDragState | null>(null)
  const [pointDragState, setPointDragState] = useState<PointDragState | null>(
    null
  )
  void projectionVersion
  const plotBounds = usePlotBounds(uPlotRef, rootRef)
  const rootRect = rootRef.current?.getBoundingClientRect()
  const overlayWidth = rootRect && rootRect.width > 0 ? rootRect.width : 800
  const overlayHeight = rootRect && rootRect.height > 0 ? rootRect.height : 400
  const seriesBySensorId = (() => {
    const u = uPlotRef.current
    if (!u) {
      return new Map<
        string,
        ReturnType<typeof getSeriesInfoFromUPlot>[number]
      >()
    }

    return new Map(
      getSeriesInfoFromUPlot(u).map((series) => [series.sensorId, series])
    )
  })()
  const visibleSensorsById = useMemo(
    () => new Map(visibleSensors.map((sensor) => [sensor.sensorId, sensor])),
    [visibleSensors]
  )

  useEffect(() => {
    const annotationIds = new Set(
      annotations.map((annotation) => annotation.id)
    )

    setAnnotationOffsets((currentOffsets) => {
      const nextOffsets = Object.fromEntries(
        Object.entries(currentOffsets).filter(([annotationId]) =>
          annotationIds.has(annotationId)
        )
      )

      return Object.keys(nextOffsets).length ===
        Object.keys(currentOffsets).length
        ? currentOffsets
        : nextOffsets
    })

    if (bubbleDragState && !annotationIds.has(bubbleDragState.annotationId)) {
      setBubbleDragState(null)
    }
    if (pointDragState && !annotationIds.has(pointDragState.annotationId)) {
      setPointDragState(null)
    }
  }, [annotations, bubbleDragState, pointDragState])

  useEffect(() => {
    const u = uPlotRef.current
    if (!u) return

    const handleSetScale = () => {
      setProjectionVersion((currentVersion) => currentVersion + 1)
    }

    u.hooks.setScale = u.hooks.setScale ?? []
    u.hooks.setScale.push(handleSetScale)

    return () => {
      if (!u.hooks.setScale) return
      u.hooks.setScale = u.hooks.setScale.filter(
        (hook) => hook !== handleSetScale
      )
    }
  }, [uPlotRef])

  useEffect(() => {
    if (!bubbleDragState) return

    const handleMouseMove = (event: MouseEvent) => {
      const nextOffset = {
        x:
          bubbleDragState.startOffsetX +
          event.clientX -
          bubbleDragState.startClientX,
        y:
          bubbleDragState.startOffsetY +
          event.clientY -
          bubbleDragState.startClientY,
      }

      setAnnotationOffsets((currentOffsets) => ({
        ...currentOffsets,
        [bubbleDragState.annotationId]: nextOffset,
      }))
    }

    const handleMouseUp = () => {
      setBubbleDragState(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [bubbleDragState])

  const getAnchorFromClientPosition = useCallback(
    (
      clientX: number,
      clientY: number
    ): { anchorTimestamp: number; anchorYRatio: number } | null => {
      const u = uPlotRef.current
      const root = rootRef.current
      if (!u || !root || !plotBounds) return null

      const rootRect = root.getBoundingClientRect()
      const xValues = u.data[0] as ArrayLike<number> | undefined
      if (!xValues || xValues.length === 0) return null

      const mouseXOnOverlay = clientX - rootRect.left
      const mouseYOnOverlay = clientY - rootRect.top
      const relXInPlot = clamp(
        mouseXOnOverlay,
        plotBounds.overLeft,
        plotBounds.overRight
      )
      const relYInPlot = clamp(
        mouseYOnOverlay,
        plotBounds.overTop,
        plotBounds.overBottom
      )
      const xValue = u.posToVal(relXInPlot - plotBounds.overLeft, 'x')
      const nearestIndex = Math.max(
        0,
        Math.min(
          xValues.length - 1,
          findNearestIndexByTime(xValues, Number(xValue))
        )
      )
      const plotHeight = Math.max(1, plotBounds.overBottom - plotBounds.overTop)
      const anchorTimestamp = Number(xValues[nearestIndex])
      const anchorYRatio = (relYInPlot - plotBounds.overTop) / plotHeight

      return Number.isFinite(anchorTimestamp)
        ? {
            anchorTimestamp,
            anchorYRatio,
          }
        : null
    },
    [plotBounds, uPlotRef]
  )

  useEffect(() => {
    if (!pointDragState) return

    const handleMouseMove = (event: MouseEvent) => {
      const nextAnchor = getAnchorFromClientPosition(
        event.clientX,
        event.clientY
      )
      if (!nextAnchor) return

      onUpdateAnnotationAnchor(pointDragState.annotationId, nextAnchor)
    }

    const handleMouseUp = () => {
      setPointDragState(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [getAnchorFromClientPosition, onUpdateAnnotationAnchor, pointDragState])

  const handleBubbleDragStart = useCallback(
    (annotationId: string, startClientX: number, startClientY: number) => {
      const currentOffset = annotationOffsets[annotationId] ?? { x: 0, y: 0 }
      setBubbleDragState({
        annotationId,
        startClientX,
        startClientY,
        startOffsetX: currentOffset.x,
        startOffsetY: currentOffset.y,
      })
    },
    [annotationOffsets]
  )

  const handlePointDragStart = useCallback((annotationId: string) => {
    setPointDragState({ annotationId })
  }, [])

  const { renderedAnnotations } = useMemo(() => {
    const u = uPlotRef.current
    const cachedAnnotations = lastRenderedAnnotationsRef.current
    const nextRenderedAnnotations: RenderedAnnotation[] = []
    const nextVisibilityDebugStateById: Record<
      string,
      AnnotationRenderDebugState
    > = {}

    const setVisibilityState = (
      annotationId: string,
      nextState: AnnotationRenderDebugState
    ) => {
      nextVisibilityDebugStateById[annotationId] = nextState
    }

    if (!u || !plotBounds) {
      annotations.forEach((annotation) => {
        if (
          annotation.sensorId !== null &&
          !visibleSensorsById.has(annotation.sensorId)
        ) {
          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'selected-sensor-hidden-while-uplot-unavailable',
            sensorId: annotation.sensorId,
          })
          return
        }

        const cachedAnnotation = cachedAnnotations[annotation.id]
        if (cachedAnnotation) {
          nextRenderedAnnotations.push(cachedAnnotation)
          setVisibilityState(annotation.id, {
            status: 'rendered-from-cache',
            reason: !u
              ? 'uplot-unavailable-using-cache'
              : 'plot-bounds-unavailable-using-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        setVisibilityState(annotation.id, {
          status: 'hidden',
          reason: !u
            ? 'uplot-unavailable-no-cache'
            : 'plot-bounds-unavailable-no-cache',
          sensorId: annotation.sensorId,
        })
      })

      return {
        renderedAnnotations: nextRenderedAnnotations,
        visibilityDebugStateById: nextVisibilityDebugStateById,
      }
    }

    annotations.forEach((annotation) => {
      const cachedAnnotation = cachedAnnotations[annotation.id]

      try {
        const xPixelOnOverlay =
          u.valToPos(annotation.anchorTimestamp, 'x') + plotBounds.overLeft

        if (
          xPixelOnOverlay < plotBounds.overLeft ||
          xPixelOnOverlay > plotBounds.overRight
        ) {
          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'anchor-outside-current-x-window',
            sensorId: annotation.sensorId,
          })
          return
        }

        if (annotation.sensorId === null) {
          const plotHeight = Math.max(
            1,
            plotBounds.overBottom - plotBounds.overTop
          )
          nextRenderedAnnotations.push({
            ...annotation,
            name: 'Select a pen',
            color: 'var(--foreground)',
            unit: undefined,
            timestamp: annotation.anchorTimestamp,
            value: 0,
            xPixelOnOverlay,
            yPixelOnOverlay: clamp(
              plotBounds.overTop + annotation.anchorYRatio * plotHeight,
              plotBounds.overTop,
              plotBounds.overBottom
            ),
          })
          setVisibilityState(annotation.id, {
            status: 'rendered',
            reason: 'pending-annotation-rendered',
            sensorId: annotation.sensorId,
          })
          return
        }

        if (!visibleSensorsById.has(annotation.sensorId)) {
          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'selected-sensor-hidden',
            sensorId: annotation.sensorId,
          })
          return
        }

        const series = seriesBySensorId.get(annotation.sensorId)
        const visibleSensor = visibleSensorsById.get(annotation.sensorId)
        if (series && (!series.show || series.isRegression)) {
          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: series.isRegression
              ? 'series-became-regression'
              : 'series-hidden-in-uplot',
            sensorId: annotation.sensorId,
          })
          return
        }
        if (!series) {
          if (cachedAnnotation) {
            nextRenderedAnnotations.push(cachedAnnotation)
            setVisibilityState(annotation.id, {
              status: 'rendered-from-cache',
              reason: 'series-missing-using-cache',
              sensorId: annotation.sensorId,
            })
            return
          }

          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'series-missing-no-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        const xSeries = u.data[0] as ArrayLike<number> | undefined
        const ySeries = u.data[series.dataIndex] as
          | ArrayLike<number | null | undefined>
          | undefined
        if (!xSeries || !ySeries) {
          if (cachedAnnotation) {
            nextRenderedAnnotations.push(cachedAnnotation)
            setVisibilityState(annotation.id, {
              status: 'rendered-from-cache',
              reason: 'series-data-missing-using-cache',
              sensorId: annotation.sensorId,
            })
            return
          }

          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'series-data-missing-no-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        const nearestValidIndex = findNearestValidIndex(
          xSeries,
          ySeries,
          annotation.anchorTimestamp
        )
        if (nearestValidIndex === -1) {
          if (cachedAnnotation) {
            nextRenderedAnnotations.push(cachedAnnotation)
            setVisibilityState(annotation.id, {
              status: 'rendered-from-cache',
              reason: 'no-valid-point-near-anchor-using-cache',
              sensorId: annotation.sensorId,
            })
            return
          }

          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'no-valid-point-near-anchor-no-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        const timestamp = Number(xSeries[nearestValidIndex])
        const value = ySeries[nearestValidIndex]
        if (
          !Number.isFinite(timestamp) ||
          typeof value !== 'number' ||
          Number.isNaN(value)
        ) {
          if (cachedAnnotation) {
            nextRenderedAnnotations.push(cachedAnnotation)
            setVisibilityState(annotation.id, {
              status: 'rendered-from-cache',
              reason: 'invalid-snapped-point-using-cache',
              sensorId: annotation.sensorId,
            })
            return
          }

          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'invalid-snapped-point-no-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        const snappedXPixelOnOverlay =
          u.valToPos(timestamp, 'x') + plotBounds.overLeft
        const yPixelOnOverlay =
          u.valToPos(value, series.scale) + plotBounds.overTop

        if (
          snappedXPixelOnOverlay < plotBounds.overLeft ||
          snappedXPixelOnOverlay > plotBounds.overRight ||
          yPixelOnOverlay < plotBounds.overTop ||
          yPixelOnOverlay > plotBounds.overBottom
        ) {
          setVisibilityState(annotation.id, {
            status: 'hidden',
            reason: 'snapped-point-outside-current-plot-bounds',
            sensorId: annotation.sensorId,
          })
          return
        }

        nextRenderedAnnotations.push({
          ...annotation,
          name: series.name,
          timestamp: annotation.displayTimestamp ?? timestamp,
          value: annotation.displayValue ?? value,
          color: visibleSensor?.color ?? series.color,
          unit:
            unitMapping[annotation.sensorId] ||
            sensorRanges[annotation.sensorId]?.unit ||
            undefined,
          xPixelOnOverlay: snappedXPixelOnOverlay,
          yPixelOnOverlay,
        })
        setVisibilityState(annotation.id, {
          status: 'rendered',
          reason: 'snapped-point-rendered',
          sensorId: annotation.sensorId,
        })
      } catch {
        if (cachedAnnotation) {
          nextRenderedAnnotations.push(cachedAnnotation)
          setVisibilityState(annotation.id, {
            status: 'rendered-from-cache',
            reason: 'projection-error-using-cache',
            sensorId: annotation.sensorId,
          })
          return
        }

        setVisibilityState(annotation.id, {
          status: 'hidden',
          reason: 'projection-error-no-cache',
          sensorId: annotation.sensorId,
        })
      }
    })

    return {
      renderedAnnotations: nextRenderedAnnotations,
      visibilityDebugStateById: nextVisibilityDebugStateById,
    }
  }, [
    annotations,
    plotBounds,
    sensorRanges,
    seriesBySensorId,
    uPlotRef,
    unitMapping,
    visibleSensorsById,
  ])

  useEffect(() => {
    lastRenderedAnnotationsRef.current = Object.fromEntries(
      renderedAnnotations.map((annotation) => [annotation.id, annotation])
    )
  }, [renderedAnnotations])

  return (
    <OverlayRoot ref={rootRef}>
      {isActive && (
        <>
          <InteractionLayer
            onMouseMove={onMouseMove}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
          />
        </>
      )}

      {showPersisted &&
        renderedAnnotations.map((annotation) => (
          <PositionedAnnotation
            key={annotation.id}
            annotationId={annotation.id}
            overlayWidth={overlayWidth}
            overlayHeight={overlayHeight}
            color={annotation.color}
            name={annotation.name}
            value={annotation.value}
            unit={annotation.unit}
            timestamp={annotation.timestamp}
            xPixelOnOverlay={annotation.xPixelOnOverlay}
            yPixelOnOverlay={annotation.yPixelOnOverlay}
            sensorId={annotation.sensorId}
            visibleSensors={visibleSensors}
            dragOffsetX={annotationOffsets[annotation.id]?.x ?? 0}
            dragOffsetY={annotationOffsets[annotation.id]?.y ?? 0}
            isBubbleDragging={bubbleDragState?.annotationId === annotation.id}
            isPointDragging={pointDragState?.annotationId === annotation.id}
            onBubbleDragStart={handleBubbleDragStart}
            onPointDragStart={handlePointDragStart}
            onSetSensorId={onSetAnnotationSensorId}
            onRemove={() => onRemoveAnnotation(annotation.id)}
          />
        ))}
    </OverlayRoot>
  )
}
